import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';


interface TreeNodeSkeletonProps {
  depth: number;
  count?: number;
}

/**
 * Modern skeleton loader for tree nodes with a nice pulsing animation
 * Shows while children are being fetched
 */
export function TreeNodeSkeleton({ depth, count = 3 }: TreeNodeSkeletonProps) {
  const baseIndent = 12;
  const minIndent = 16;
  const indentation = depth === 0 ? minIndent : minIndent + depth * baseIndent;

  return (
    // Testable on purpose: an expanded folder with no children renders this,
    // and a folder nothing will ever load (a closed connection) rendering it
    // forever is a real bug class — the e2e needs to be able to name it.
    <div className="animate-in fade-in-50 duration-200" data-testid="tree-node-skeleton">
      {Array.from({ length: count }).map((_, index) => (
        <div
          key={index}
          className="flex items-center px-2 py-1.5 text-sm"
          style={{
            paddingLeft: `${indentation}px`,
            animationDelay: `${index * 50}ms`
          }}
        >
          <div className="flex items-center gap-2 w-full">
            {/* Skeleton icon */}
            <div
              className="h-4 w-4 rounded bg-muted animate-pulse"
              style={{ animationDelay: `${index * 100}ms` }}
            />
            {/* Skeleton text - varying widths for natural look */}
            <div
              className="h-3.5 rounded bg-muted animate-pulse"
              style={{
                width: `${60 + (index % 3) * 30}px`,
                animationDelay: `${index * 100 + 50}ms`
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Inline loading indicator with spinner - used for single item loading
 */
export function TreeNodeLoadingIndicator({ depth }: { depth: number }) {
  const baseIndent = 12;
  const minIndent = 16;
  const indentation = depth === 0 ? minIndent : minIndent + depth * baseIndent;

  return (
    <div
      className="flex items-center px-2 py-1.5 text-sm animate-in fade-in-50 duration-200"
      style={{ paddingLeft: `${indentation}px` }}
    >
      <div className="flex items-center gap-2 text-muted-foreground">
        <RandomDotmSpinner size={14} className="opacity-90" />
        <span className="text-xs">Loading...</span>
      </div>
    </div>
  );
}
